import { useEffect, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ContatoPage = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    assunto: '',
    mensagem: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    document.title = 'Contato Campinas Crédito Consignado Carbone Soluções';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Entre em contato com a Carbone Soluções Financeiras em Campinas. Atendimento especializado em crédito consignado, empréstimo INSS, crédito CLT, portabilidade e financiamento. Tire suas dúvidas e solicite uma proposta personalizada.');
    }

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contato - Carbone Soluções Financeiras",
      "description": "Entre em contato com a Carbone Soluções Financeiras para soluções em crédito consignado",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contato`
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validação
    if (!formData.nome || !formData.email || !formData.telefone || !formData.assunto || !formData.mensagem) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    if (formData.mensagem.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const formBody = new URLSearchParams();
      formBody.append('nome', formData.nome);
      formBody.append('email', formData.email);
      formBody.append('telefone', formData.telefone);
      formBody.append('assunto', formData.assunto);
      formBody.append('mensagem', formData.mensagem);

      const response = await fetch('https://readdy.ai/api/form/d60eangiku00ff9vl490', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nome: '',
          email: '',
          telefone: '',
          assunto: '',
          mensagem: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-[#1a1a2e] mb-6">
              Entre em <strong className="text-[#d4af37]">Contato</strong>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Estamos prontos para atender você. Preencha o formulário abaixo ou utilize nossos canais de atendimento direto.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Informações de Contato */}
            <div className="space-y-8">
              <div className="bg-gradient-to-br from-[#1a1a2e] to-[#2d2d44] rounded-2xl p-8 text-white">
                <h2 className="text-2xl font-bold mb-6">Informações de Contato</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37] rounded-lg flex-shrink-0">
                      <i className="ri-phone-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Telefone</h3>
                      <a href="tel:+5519971344445" className="text-gray-300 hover:text-[#d4af37] transition-colors cursor-pointer">
                        (19) 97134-4445
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37] rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">E-mail</h3>
                      <a href="mailto:contato@carbonesolucoesfinanceiras.com.br" className="text-gray-300 hover:text-[#d4af37] transition-colors cursor-pointer break-all">
                        contato@carbonesolucoesfinanceiras.com.br
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37] rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Localização</h3>
                      <p className="text-gray-300">Campinas, São Paulo</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37] rounded-lg flex-shrink-0">
                      <i className="ri-time-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Horário de Atendimento</h3>
                      <p className="text-gray-300">Segunda a Sexta: 08:00 - 18:00</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-white/20">
                  <h3 className="font-semibold mb-4">Redes Sociais</h3>
                  <div className="flex gap-4">
                    <a
                      href="https://www.instagram.com/carbonesolucoesfinanceiras/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-[#d4af37] rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-instagram-line text-2xl"></i>
                    </a>
                    <a
                      href="https://www.facebook.com/profile.php?id=61586501853205"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-[#d4af37] rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-facebook-fill text-2xl"></i>
                    </a>
                    <a
                      href="https://wa.me/5519971344445?text=Olá! Gostaria de falar com um especialista."
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-[#25D366] rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-whatsapp-line text-2xl"></i>
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-[#1a1a2e] mb-4">Por que escolher a Carbone?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#d4af37] text-xl mt-1"></i>
                    <span className="text-gray-700">Atendimento humanizado e personalizado</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#d4af37] text-xl mt-1"></i>
                    <span className="text-gray-700">Melhores taxas do mercado</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#d4af37] text-xl mt-1"></i>
                    <span className="text-gray-700">Processo rápido e transparente</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#d4af37] text-xl mt-1"></i>
                    <span className="text-gray-700">Especialistas em crédito consignado</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Formulário de Contato */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h2 className="text-2xl font-bold text-[#1a1a2e] mb-6">Envie sua Mensagem</h2>
              
              <form id="contato-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="nome" className="block text-sm font-semibold text-gray-700 mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm"
                    placeholder="Seu nome completo"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm"
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="telefone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Telefone *
                  </label>
                  <input
                    type="tel"
                    id="telefone"
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm"
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <div>
                  <label htmlFor="assunto" className="block text-sm font-semibold text-gray-700 mb-2">
                    Assunto *
                  </label>
                  <select
                    id="assunto"
                    name="assunto"
                    value={formData.assunto}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="">Selecione um assunto</option>
                    <option value="Crédito Consignado INSS">Crédito Consignado INSS</option>
                    <option value="Crédito Consignado CLT">Crédito Consignado CLT</option>
                    <option value="Portabilidade">Portabilidade</option>
                    <option value="Refinanciamento">Refinanciamento</option>
                    <option value="Financiamento de Veículos">Financiamento de Veículos</option>
                    <option value="Financiamento de Imóveis">Financiamento de Imóveis</option>
                    <option value="Dúvidas Gerais">Dúvidas Gerais</option>
                    <option value="Outros">Outros</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="mensagem" className="block text-sm font-semibold text-gray-700 mb-2">
                    Mensagem * <span className="text-gray-500 font-normal">(máximo 500 caracteres)</span>
                  </label>
                  <textarea
                    id="mensagem"
                    name="mensagem"
                    value={formData.mensagem}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all resize-none text-sm"
                    placeholder="Descreva como podemos ajudá-lo..."
                  ></textarea>
                  <div className="text-right text-xs text-gray-500 mt-1">
                    {formData.mensagem.length}/500 caracteres
                  </div>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-3">
                    <i className="ri-check-line text-xl"></i>
                    <span className="text-sm">Mensagem enviada com sucesso! Entraremos em contato em breve.</span>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-3">
                    <i className="ri-error-warning-line text-xl"></i>
                    <span className="text-sm">Erro ao enviar mensagem. Verifique os campos e tente novamente.</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#d4af37] hover:bg-[#c49d2f] text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Enviando...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-send-plane-fill"></i>
                      Enviar Mensagem
                    </span>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ContatoPage;
